import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../../services/api';
import { useCreditConfig } from '../../contexts/CreditConfigContext';
import { useAuthCheck } from '../../hooks/useAuthCheck';
import { useI18n } from '../../contexts/I18nContext';
import { usePolling } from '../../hooks/usePolling';
import { handleUnifiedError, showSuccessToast } from '../../utils/unifiedErrorHandler';
import { API_BASE_URL } from '../../lib/constants';
import './ImageEditingPanel.scss';
import ImageEditingPanelView from './ImageEditingPanelView';

const ImageEditingPanel = () => {
  const { t } = useI18n();
  const location = useLocation();
  const { getToolDefaultCost, getImageGenerationCredits } = useCreditConfig();
  
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationCount] = useState(1); // Fixed value since setGenerationCount was unused
  const [selectedSize, setSelectedSize] = useState('Auto');
  const [updateRecordId, setUpdateRecordId] = useState(null);
  const [showUploadErrorModal, setShowUploadErrorModal] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMediaUrl, setShareMediaUrl] = useState('');
  const creditsPerImage = getImageGenerationCredits({
    toolId: 'image-to-image',
    model: selectedSize && typeof selectedSize === 'object' ? selectedSize.model : undefined,
    resolution: selectedSize && typeof selectedSize === 'object' ? selectedSize.resolution : undefined
  }) || getToolDefaultCost();
  
  // 使用统一的认证检查Hook
  const { checkAuthAndExecute, showLoginModal, handleCloseLogin, handleLoginSuccess } = useAuthCheck();

  // 使用公共轮询Hook
  const { startPolling, stopPolling } = usePolling({
    maxAttempts: 60,
    pollInterval: 3000,
    initialDelay: 2000,
    onSuccess: (data) => {
      setGenerationProgress(100);
      setTimeout(() => {
        setGeneratedImage({ url: data.imageUrl, id: data.id });
        setShareMediaUrl(data.shareUrl || '');
        setIsGenerating(false);
        setGenerationProgress(0);
      }, 500);
      showSuccessToast(t('aiWorkbench.generateSuccess'));
    },
    onFailed: (result) => {
      setIsGenerating(false);
      setGenerationProgress(0);
      // usePolling已经显示了错误toast，这里不需要重复显示
    },
    onError: () => {
      setIsGenerating(false);
      setGenerationProgress(0);
    },
    onTimeout: () => {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  });

  // 超时保护机制：防止进度条无限卡住
  useEffect(() => {
    let timeoutId;
    if (isGenerating) {
      timeoutId = setTimeout(() => {
        console.warn('Image generation timeout after 5 minutes, forcing reset');
        setIsGenerating(false);
        setGenerationProgress(0);
        stopPolling();
        handleUnifiedError(new Error(t('aiToolTemplate.generationTimeout')), t, {
          showToast: true,
          severity: 'warning'
        });
      }, 180000);
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isGenerating, stopPolling, t]);

  // 处理URL参数，从创作记录回显数据
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const recordId = urlParams.get('updateRecordId');
    const promptParam = urlParams.get('prompt');
    const imageUrls = urlParams.get('imageUrls');
    
    if (recordId) {
      setUpdateRecordId(recordId);
    }
    
    if (promptParam) {
      setPrompt(decodeURIComponent(promptParam));
    }
    
    // 如果有图片URL参数，尝试加载图片
    if (imageUrls) {
      const urls = imageUrls.split(',');
      loadImagesFromUrls(urls);
    }
  }, [location.search]);

  // 从URL加载图片
  const loadImagesFromUrls = async (urls) => {
    const loadedImages = [];
    
    for (let i = 0; i < urls.length && i < 5; i++) {
      try {
        const url = urls[i].trim();
        if (!url) continue;
        
        // 确保使用完整的URL
        const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
        
        // 获取图片数据
        const response = await fetch(fullUrl);
        const blob = await response.blob();
        
        // 创建File对象
        const file = new File([blob], `image_${i + 1}.png`, { type: blob.type || 'image/png' });
        
        // 创建预览URL
        const preview = URL.createObjectURL(blob);
        
        const imageObj = {
          id: Date.now() + Math.random() + i,
          file: file,
          preview: preview,
          name: file.name
        };
        
        loadedImages.push(imageObj);
      } catch (error) {
        console.error('Failed to load image from URL:', urls[i], error);
      }
    }
    
    if (loadedImages.length > 0) {
      setUploadedImages(loadedImages);
    }
  };

  const handleFiles = useCallback((files) => {
    const validFiles = files.filter(file => {
      const isValidType = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'].includes(file.type);
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
      return isValidType && isValidSize;
    });

    if (validFiles.length !== files.length) {
      setShowUploadErrorModal(true);
    }

    const remainingSlots = 5 - uploadedImages.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);

    if (filesToAdd.length < validFiles.length) {
      alert(t('aiWorkbench.slotsRemainingError', { slots: remainingSlots }));
    }

    filesToAdd.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          id: Date.now() + Math.random(),
          file: file,
          preview: e.target.result,
          name: file.name
        };
        setUploadedImages(prev => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    });
  }, [uploadedImages.length, t]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, [handleFiles]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const removeImage = (imageId) => {
    setUploadedImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleGenerateImage = async () => {
    if (uploadedImages.length === 0) {
      handleUnifiedError(new Error(t('aiWorkbench.uploadImageFirst')), t, {
        showToast: true,
        severity: 'warning'
      });
      return;
    }

    if (!prompt.trim()) {
      handleUnifiedError(new Error('Please describe the effect you want'), t, {
        showToast: true,
        severity: 'warning'
      });
      return;
    }
    
    // 使用统一的认证检查
    checkAuthAndExecute(async () => {
      setIsGenerating(true);
      setGenerationProgress(0);
    
      try {
        const formData = new FormData();
        
        let finalPrompt = prompt.trim();
        if (selectedStyle) {
          const stylePrompts = {
            'Enhance Details': t('aiWorkbench.stylePrompts.enhanceDetails'),
            'Art Style': t('aiWorkbench.stylePrompts.artStyle'),
            'Anime Style': t('aiWorkbench.stylePrompts.animeStyle'),
            'Realistic Photo': t('aiWorkbench.stylePrompts.realisticPhoto')
          };
          const styleDescription = stylePrompts[selectedStyle] || selectedStyle;
          
          if (finalPrompt) {
            finalPrompt = `${finalPrompt}, ${styleDescription}`;
          } else {
            finalPrompt = styleDescription;
          }
        }
        let aspectRatio = selectedSize || 'Auto';
        let model;
        let resolution;
        if (selectedSize && typeof selectedSize === 'object') {
          aspectRatio = selectedSize.aspectRatio || 'Auto';
          model = selectedSize.model;
          resolution = selectedSize.resolution;
        }
        if (aspectRatio && typeof aspectRatio === 'object') {
          aspectRatio = aspectRatio.aspectRatio || 'Auto';
        }

        formData.append('toolId', 'image-to-image');
        formData.append('prompt', finalPrompt);
        formData.append('style', selectedStyle);
        formData.append('aspectRatio', aspectRatio);
        if (model) formData.append('model', model);
        if (resolution) formData.append('resolution', resolution);
        
        uploadedImages.forEach((image, index) => {
          formData.append('images', image.file);
        });

        // 如果有updateRecordId，添加到请求中
        if (updateRecordId) {
          formData.append('updateRecordId', updateRecordId);
        }

        const response = await axios.post('/generate-image/edit', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const data = response.data;
        const generationId = data.id;
        
        // 使用公共轮询Hook开始轮询
        startPolling(generationId, (progress) => {
          setGenerationProgress(progress);
        });
      } catch (error) {
        // 确保在任何错误情况下都重置进度条和生成状态
        setIsGenerating(false);
        setGenerationProgress(0);
        
        // 停止可能正在进行的轮询
        stopPolling();
        
        console.error('Image generation failed:', error);
        handleUnifiedError(error, t, {
          showToast: true,
          fallbackMessage: t('aiWorkbench.generationFailed')
        });
      }
    });
  };

  return (
    <ImageEditingPanelView
      t={t}
      uploadedImages={uploadedImages}
      isDragOver={isDragOver}
      handleDragOver={handleDragOver}
      handleDragLeave={handleDragLeave}
      handleDrop={handleDrop}
      handleFileSelect={handleFileSelect}
      removeImage={removeImage}
      selectedStyle={selectedStyle}
      setSelectedStyle={setSelectedStyle}
      prompt={prompt}
      setPrompt={setPrompt}
      selectedSize={selectedSize}
      setSelectedSize={setSelectedSize}
      generationCount={generationCount}
      creditsPerImage={creditsPerImage}
      isGenerating={isGenerating}
      generationProgress={generationProgress}
      handleGenerateImage={handleGenerateImage}
      generatedImage={generatedImage}
      shareMediaUrl={shareMediaUrl}
      shareOpen={shareOpen}
      setShareOpen={setShareOpen}
      showLoginModal={showLoginModal}
      handleCloseLogin={handleCloseLogin}
      handleLoginSuccess={handleLoginSuccess}
      showUploadErrorModal={showUploadErrorModal}
      setShowUploadErrorModal={setShowUploadErrorModal}
    />
  );
};

export default ImageEditingPanel;
